import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import isNil from "lodash/isNil";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { MultilineTextField } from "components/MultilineTextField";
import { TextField } from "components/TextField";
import { DatePicker } from "components/DatePicker";
import { Dropdown } from "components/Dropdown";

import { useFetchGet } from "hooks/fetch/useFetchGet";
import { useFetchPost } from "hooks/fetch/useFetchPost";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { objectToQueryString } from "core/utils/query_string";

import { UNKNOWN } from "core/constants";

import { styled } from "@mui/material/styles";
import { START_OF_TODAY_DATE } from "core/date-helpers";

const validationSchema = Yup.object({
  summary: Yup.string()
    .label("Summary")
    .required(),
  assetId: Yup.number()
    .label("Asset Id")
    .required(),
  requestedCompletionDate: Yup.date()
    .label("Requested completion date")
    .required()
    .typeError("Requested completion date must be a valid date")
    .min(
      START_OF_TODAY_DATE,
      "Target completion date cannot be before the current date"
    ),
  problemDescription: Yup.string()
    .label("Problem description")
    .required()
});

export function NewWorkOrderDialog({
  open,
  setOpen,
  caseObject,
  site,
  machine,
  component,
  failureMode,
  refetchStream
}) {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    loading: workOrderAssetsLoading,
    error: workOrderAssetsError,
    response: workOrderAssets
  } = useFetchGet(
    "work_order_assets?" +
      objectToQueryString({
        machine_id__in: machine?.id,
        ordering: "name"
      }),
    isNil(machine?.id)
  );

  const onCompleted = async () => {
    addSnackbarToQueue({
      message: "Work Request created successfully.",
      severity: "success"
    });
    refetchStream();
    setOpen(false);
  };

  const onError = () => {
    addSnackbarToQueue({
      message: "Work Request failed to be created.",
      severity: "error"
    });
    setOpen(false);
  };

  const {
    postCallback: createWorkOrder,
    loading: createWorkOrderLoading
  } = useFetchPost("work_orders", onCompleted, onError);

  const formik = useFormik({
    initialValues: {
      summary: "",
      assetId: "",
      requestedCompletionDate: null,
      problemDescription: ""
    },
    validationSchema,
    onSubmit: values => {
      createWorkOrder({
        status: "Awaiting Sync",
        summary: values.summary,
        asset: values.assetId,
        requested_completion_date: values.requestedCompletionDate?.toISOString(),
        problem_description: values.problemDescription,
        external_id: null,
        last_updated_externally: null,
        case: caseObject.id
      });
    }
  });

  useEffect(() => {
    const siteName = site?.name ?? UNKNOWN;
    const machineName = machine?.name ?? UNKNOWN;
    const componentName = component?.name ?? UNKNOWN;
    const failureModeName = failureMode?.name;
    const description = caseObject?.description ?? UNKNOWN;
    const url = window.location.href;

    // Only add a new line for failure mode when it exists
    const failureModeString = failureModeName ? `\n${failureModeName}` : "";

    formik?.setFieldValue(
      "problemDescription",
      `${siteName} - ${machineName} - ${componentName}${failureModeString}\n${description}\nCase page: ${url}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caseObject?.description,
    component?.name,
    failureMode?.name,
    machine?.name,
    site?.name
  ]);

  const setRequestedCompletionDate = value => {
    formik.setFieldValue("requestedCompletionDate", value);
  };

  const closeDialog = () => {
    formik.resetForm();
    setOpen(false);
  };

  const isLoading = createWorkOrderLoading || workOrderAssetsLoading;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-label="new work order dialog"
      data-qa="CP-Date-Picker-Case-Open-Date"
    >
      <DialogTitleContainer>
        <Header>Create Work Request</Header>
        <CloseButton
          onClick={() => closeDialog()}
          data_cy="CP-Button-Close-Dialog"
        >
          <CloseIcon />
        </CloseButton>
      </DialogTitleContainer>
      <DialogContent dividers>
        <form
          id="new-work-order-form"
          data-qa="CP-Form-New-Work-Order"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <Grid container>
            <GridWithGap item xs={12}>
              <Dropdown
                id="assetId"
                name="assetId"
                label="Asset ID"
                value={formik.values.assetId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={
                  workOrderAssetsLoading ||
                  !workOrderAssets?.results?.length ||
                  workOrderAssetsError
                }
                data-qa="CP-Select-Work-Order-AssetID"
                error={formik.touched.assetId && Boolean(formik.errors.assetId)}
                helperText={
                  formik.touched.assetId && formik.errors.assetId
                    ? formik.errors.assetId
                    : workOrderAssets?.results?.length === 0
                    ? `No assets found for this machine ${machine?.name}`
                    : ""
                }
                fullWidth
                required
              >
                {workOrderAssets?.results?.map(({ name, id }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                )) ?? []}
              </Dropdown>
            </GridWithGap>
            <GridWithGap item xs={12}>
              <DatePicker
                id="requestedCompletionDate"
                date={formik.values.requestedCompletionDate}
                onChange={setRequestedCompletionDate}
                label="Requested Completion Date"
                ariaLabel="nwo-dialog-requested-completion-date-picker"
                disableFuture={false}
                minDate={START_OF_TODAY_DATE}
                data-qa="CP-Date-Work-Order-Completion-Date"
                slotProps={{
                  textField: {
                    onBlur: formik.handleBlur,
                    error:
                      formik.touched.requestedCompletionDate &&
                      Boolean(formik.errors.requestedCompletionDate),
                    helperText:
                      formik.touched.requestedCompletionDate &&
                      formik.errors.requestedCompletionDate
                  }
                }}
                required
                fullWidth
                clearable
              />
            </GridWithGap>
            <GridWithGap item xs={12}>
              <TextField
                id="nwo-form-summary"
                name="summary"
                label="Summary"
                value={formik.values.summary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.summary && Boolean(formik.errors.summary)}
                helperText={formik.touched.summary && formik.errors.summary}
                data-qa="CP-Text-Work-Order-Summary"
                required
                fullWidth
              />
            </GridWithGap>
            <GridWithGap item xs={12}>
              <MultilineTextField
                id="nwo-form-problem-description"
                name="problemDescription"
                label="Problem Description"
                value={formik.values.problemDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.problemDescription &&
                  Boolean(formik.errors.problemDescription)
                }
                helperText={
                  formik.touched.problemDescription &&
                  Boolean(formik.errors.problemDescription) &&
                  formik.errors.problemDescription
                }
                data-qa="CP-Select-Work-Order-Description"
                fullWidth
                required
              />
            </GridWithGap>
          </Grid>
        </form>
      </DialogContent>
      <ButtonArea>
        <ActionButton
          onClick={() => closeDialog()}
          disabled={isLoading}
          variant="outlined"
          color="primary"
          data-qa="CP-Button-Cancel-Work-Order"
        >
          Cancel
        </ActionButton>
        <ActionButton
          type="submit"
          form="new-work-order-form"
          disabled={isLoading}
          color="primary"
          variant="contained"
          data-qa="CP-Button-Submit-Work-Order"
        >
          Submit
        </ActionButton>
      </ButtonArea>
    </Dialog>
  );
}

const DialogTitleContainer = styled(DialogTitle)`
  padding: ${({ theme }) =>
    `${theme.spacing(2.5)} 0 ${theme.spacing(1.25)} ${theme.spacing(2.5)}`};

  & > h2 {
    font-weight: 600;
    font-size: 1.6rem;
  }
`;

const Header = styled(Typography)`
  text-transform: none;
  font-weight: bold;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 10px;
  top: 10px;
`;

const GridWithGap = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(2)};

  &:first-child {
    margin-top: 0;
  }
`;

const ButtonArea = styled(DialogActions)`
  height: 64px;
`;

const ActionButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
`;

NewWorkOrderDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  caseObject: PropTypes.object,
  site: PropTypes.object,
  machine: PropTypes.object,
  component: PropTypes.object,
  failureMode: PropTypes.object,
  setNotification: PropTypes.func,
  refetchStream: PropTypes.func
};
